<script setup lang="ts">
import type { Comment, CommentSubmittedPayload, Content, PageResult } from '~/types/blog'
import { buildLocalSubmittedComment, insertLocalComment } from '~/composables/commentLocalInsert'
import { scrollToElementWithOffset } from '~/composables/useAnchorScroll'
import { normalizeFieldBool, normalizeFieldNumber, normalizeFieldText } from '~/composables/useCustomFields'
import { resolveHtmlAssetUrls } from '~/composables/useAssetUrl'
import { useEmbeddedPostCards } from '~/composables/useEmbeddedPostCards'
import { bindProgressiveImages } from '~/composables/useProgressiveImages'
import { resolvePagePath } from '~/composables/usePermalink'
import { useCommentAnchorScroll } from '~/composables/useCommentAnchorScroll'

const route = useRoute()
const blogApi = useBlogApi()
const { isStaleNavigation } = useNavigationStaleGuard()
const siteData = await useSiteData()
const siteBaseUrl = resolveSiteBaseUrl(siteData?.setting?.customFields)
const pathParsed = resolvePagePath(siteData?.setting?.customFields, route.path)
const routeSlug = String(route.params.slug || '')
const slug = String(routeSlug || pathParsed?.slug || '')
const cid = Number(pathParsed?.cid || 0)
const createStaleContentFallback = (): Content => ({
  ID: 0,
  title: '',
  slug: '',
  type: 'page',
  status: 'draft'
})

const { data: content } = await useAsyncData(`standalone-page:${route.path}`, async (_nuxtApp, { signal }) => {
  if (slug) {
    try {
      return await blogApi.getContentBySlug(slug, { signal })
    } catch (error) {
      if (isStaleNavigation() || isAbortLikeError(error)) {
        return createStaleContentFallback()
      }
      if (!cid) {
        throw error
      }
    }
  }
  if (cid > 0) {
    try {
      return await blogApi.getContent(cid, { signal })
    } catch (error) {
      if (isStaleNavigation() || isAbortLikeError(error)) {
        return createStaleContentFallback()
      }
      throw error
    }
  }
  if (isStaleNavigation()) {
    return createStaleContentFallback()
  }
  if (import.meta.client) {
    return createStaleContentFallback()
  }
  throw createError({ status: 404, message: '独立页面不存在' })
})

if ((!content.value?.ID || content.value.type !== 'page') && !isStaleNavigation()) {
  if (import.meta.server) {
    throw createError({ status: 404, message: '独立页面不存在' })
  }
  showError({ statusCode: 404, message: '独立页面不存在' })
}

const settingsFields = siteData?.setting?.customFields
const commentListMode = computed(() => {
  const mode = normalizeFieldText(settingsFields, 'commentListMode', 'pager').toLowerCase()
  return mode === 'pager' || mode === 'pagination' ? 'pager' : 'infinite'
})
const isCommentPagerMode = computed(() => commentListMode.value === 'pager')
const commentPageSize = computed(() => {
  const size = Math.floor(normalizeFieldNumber(settingsFields, 'commentPageSize', 10))
  return Math.min(100, Math.max(1, size || 10))
})
const currentCommentPage = computed(() => {
  if (!isCommentPagerMode.value) {
    return 1
  }
  const raw = Number(route.query.cpage || 1)
  if (!Number.isFinite(raw) || raw < 1) {
    return 1
  }
  return Math.floor(raw)
})
const requestCommentPage = computed(() => (isCommentPagerMode.value ? currentCommentPage.value : 1))
const commentDataKey = computed(() =>
  `page-comments:${content.value?.ID}:${commentListMode.value}:${requestCommentPage.value}:${commentPageSize.value}`
)
const { data: firstComments, pending: firstCommentsPending } = await useAsyncData<PageResult<Comment>>(
  commentDataKey,
  () => {
    const contentID = Number(content.value?.ID || 0)
    if (contentID <= 0) {
      return Promise.resolve({
        list: [],
        total: 0,
        page: requestCommentPage.value,
        pageSize: commentPageSize.value
      })
    }
    return blogApi.getCommentTree(contentID, { page: requestCommentPage.value, pageSize: commentPageSize.value })
  }
)
const comments = ref<Comment[]>([])
const commentPage = ref(1)
const commentTotal = ref(0)
const commentLoading = ref(false)
const commentNoMore = ref(false)
let commentScrollFrame = 0
const hasMoreComments = computed(() => !isCommentPagerMode.value && !commentNoMore.value)
const showCommentPagerSkeleton = computed(() => isCommentPagerMode.value && firstCommentsPending.value)
const showCommentInfiniteSkeleton = computed(() => !isCommentPagerMode.value && firstCommentsPending.value && !comments.value.length)
const allowComment = computed(() => content.value?.allowComment !== false)
const hasComments = computed(() => comments.value.length > 0)

const replyingTo = ref<Comment | null>(null)
const onReply = (item: Comment) => {
  replyingTo.value = item
  if (import.meta.client) {
    nextTick(() => {
      const replyForm = document.getElementById(`reply-form-${item.ID}`)
      const target = replyForm || document.getElementById(`comment-${item.ID}`)
      scrollToElementWithOffset(target)
    })
  }
}
const clearReply = () => {
  replyingTo.value = null
}

let tempCommentID = 0

const nextTempCommentID = () => {
  tempCommentID -= 1
  return tempCommentID
}

const resolveReplyTarget = (parentID: number) => {
  for (let rootIndex = 0; rootIndex < comments.value.length; rootIndex += 1) {
    const root = comments.value[rootIndex]
    if (root.ID === parentID) {
      return { rootIndex, parent: root }
    }
    const child = (root.children || []).find(item => item.ID === parentID)
    if (child) {
      return { rootIndex, parent: child }
    }
  }
  return null
}

const onCommentSubmitted = (payload: CommentSubmittedPayload, replyItem: Comment | null = null) => {
  const parentID = Number(payload.parentId || 0)
  const target = parentID > 0 ? resolveReplyTarget(parentID) : null
  const node = buildLocalSubmittedComment(payload, replyItem || target?.parent || null, nextTempCommentID)
  comments.value = insertLocalComment(comments.value, node, parentID)

  commentTotal.value += 1
  if (content.value) {
    content.value.commentCount = Number(content.value.commentCount || 0) + 1
  }
  clearReply()
}

const siteName = normalizeFieldText(siteData?.setting?.customFields, 'siteName')
const siteDescription = normalizeFieldText(
  siteData?.setting?.customFields,
  'siteDescription',
  normalizeFieldText(siteData?.setting?.customFields, 'seoDescription', '')
)
const pageDescription = computed(() => content.value?.summary || siteDescription)
const gravatarMirror = normalizeFieldText(siteData?.setting?.customFields, 'gravatarMirror', 'https://www.gravatar.com/avatar')
const isMarkdownPage = computed(() => !!String(content.value?.markdown || '').trim())
const htmlBody = computed(() => resolveHtmlAssetUrls(content.value?.content || ''))
const seoTitle = computed(() => `${content.value?.title || '页面'} - ${siteName}`)
const htmlViewRef = ref<HTMLElement | null>(null)
const showToc = computed(() => normalizeFieldBool(content.value?.customFields, 'showToc', true))
const { tocItems, tocBoxRef, tocCollapsed, hasToc, onTocChange, refreshFromElement, toggleToc } = useContentToc({ enabled: showToc })
const { postCards } = useEmbeddedPostCards({
  source: () => isMarkdownPage.value ? content.value?.markdown || '' : '',
  fields: settingsFields,
  siteBaseUrl
})
const liked = ref(false)
const likeEffectToken = ref(0)
const contentLike = useOptimisticLike('content', blogApi.likeContent)
let releaseHtmlProgressiveImages: (() => void) | null = null

const syncLikedState = () => {
  liked.value = contentLike.isLiked(content.value)
}

const bindHtmlProgressiveImages = () => {
  if (isMarkdownPage.value) {
    releaseHtmlProgressiveImages?.()
    releaseHtmlProgressiveImages = null
    return
  }
  releaseHtmlProgressiveImages?.()
  releaseHtmlProgressiveImages = bindProgressiveImages(htmlViewRef.value)
}

const refreshHtmlToc = () => {
  if (isMarkdownPage.value) {
    return
  }
  refreshFromElement(htmlViewRef.value)
}

const resetCommentPage = (payload?: PageResult<Comment> | null) => {
  const list = payload?.list || []
  commentTotal.value = Number(payload?.total || 0)
  comments.value = [...list]
  commentPage.value = isCommentPagerMode.value ? currentCommentPage.value + 1 : 2
  commentNoMore.value = isCommentPagerMode.value
    ? true
    : comments.value.length >= commentTotal.value || list.length < commentPageSize.value
}

const loadMoreComments = async () => {
  if (isCommentPagerMode.value || commentLoading.value || commentNoMore.value) {
    return
  }
  const contentID = Number(content.value?.ID || 0)
  if (contentID <= 0) {
    commentNoMore.value = true
    return
  }
  commentLoading.value = true
  try {
    const batch = await blogApi.getCommentTree(contentID, { page: commentPage.value, pageSize: commentPageSize.value })
    const next = batch.list || []
    commentTotal.value = Number(batch.total || 0)
    comments.value = [...comments.value, ...next]
    if (comments.value.length >= commentTotal.value || next.length < commentPageSize.value) {
      commentNoMore.value = true
      return
    }
    commentPage.value += 1
  } catch {
    // 网络异常时停止自动滚动加载，避免反复重试。
    commentNoMore.value = true
  } finally {
    commentLoading.value = false
  }
}

const onCommentScroll = () => {
  if (!import.meta.client || isCommentPagerMode.value || commentLoading.value || commentNoMore.value || !comments.value.length) {
    return
  }
  if (commentScrollFrame) {
    return
  }
  commentScrollFrame = window.requestAnimationFrame(() => {
    commentScrollFrame = 0
    if (isCommentPagerMode.value || commentLoading.value || commentNoMore.value || !comments.value.length) {
      return
    }
    const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 140
    if (nearBottom) {
      void loadMoreComments()
    }
  })
}

const { onDocumentAnchorClick, settleRouteHashVisible } = useCommentAnchorScroll({
  comments,
  contentId: () => content.value?.ID || 0,
  commentPageSize,
  isCommentPagerMode,
  currentCommentPage,
  commentNoMore,
  loadMoreComments
})

watch(
  () => firstComments.value,
  value => {
    resetCommentPage(value || null)
    if (import.meta.client && route.hash) {
      nextTick(() => {
        void settleRouteHashVisible(route.hash, 'auto')
      })
    }
  },
  { immediate: true }
)

const likeCurrentContent = () => {
  contentLike.like(content.value, () => {
    liked.value = true
    likeEffectToken.value += 1
  })
}

onMounted(() => {
  syncLikedState()
  window.addEventListener('scroll', onCommentScroll, { passive: true })
  document.addEventListener('click', onDocumentAnchorClick)
  if (route.hash) {
    nextTick(() => {
      void settleRouteHashVisible(route.hash, 'auto')
    })
  }
  nextTick(() => {
    bindHtmlProgressiveImages()
    refreshHtmlToc()
  })
})

watch(
  () => route.hash,
  hash => {
    if (!import.meta.client || !hash) {
      return
    }
    nextTick(() => {
      void settleRouteHashVisible(hash, 'smooth')
    })
  }
)

watch(
  () => [htmlBody.value, isMarkdownPage.value, showToc.value],
  () => {
    nextTick(() => {
      bindHtmlProgressiveImages()
      refreshHtmlToc()
    })
  }
)

watch(
  () => content.value?.ID,
  () => syncLikedState()
)

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onCommentScroll)
  document.removeEventListener('click', onDocumentAnchorClick)
  if (commentScrollFrame) {
    window.cancelAnimationFrame(commentScrollFrame)
    commentScrollFrame = 0
  }
  releaseHtmlProgressiveImages?.()
  releaseHtmlProgressiveImages = null
})

useSeoMeta({
  title: () => seoTitle.value,
  description: () => pageDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => pageDescription.value
})

useHead({
  link: [{ rel: 'canonical', href: `${siteBaseUrl}${route.path}` }]
})
</script>

<template>
  <article class="card page-detail">
    <header>
      <h1>{{ content?.title }}</h1>
      <div class="meta-row">
        <span class="meta-item"><i class="fa-regular fa-calendar" aria-hidden="true" />{{ content?.publishedAt ? new Date(content.publishedAt).toLocaleString('zh-CN') : '未发布' }}</span>
        <span class="meta-item"><i class="fa-regular fa-user" aria-hidden="true" />{{ content?.authorName || '佚名' }}</span>
        <span class="meta-item"><i class="fa-regular fa-eye" aria-hidden="true" />{{ content?.viewCount || 0 }} 阅读</span>
        <span class="meta-item"><i class="fa-regular fa-heart" aria-hidden="true" />{{ content?.likeCount || 0 }} 点赞</span>
        <span class="meta-item"><i class="fa-regular fa-comment" aria-hidden="true" />{{ content?.commentCount || 0 }} 评论</span>
      </div>
    </header>

    <div v-if="hasToc" ref="tocBoxRef" class="toc-box">
      <BlogPostToc
        :items="tocItems"
        :collapsed="tocCollapsed"
        collapsible
        @toggle="toggleToc"
      />
    </div>

    <BlogMarkdownContent
      v-if="isMarkdownPage"
      :source="content?.markdown || ''"
      :toc-enabled="showToc"
      :post-cards="postCards"
      @toc-change="onTocChange"
    />
    <div v-else ref="htmlViewRef" class="html-view" v-html="htmlBody" />
    <BlogAttachmentDownloads :attachments="content?.attachments" />
    <div class="page-tools">
      <BlogLikeButton
        variant="normal"
        :liked="liked"
        :effect-token="likeEffectToken"
        @click="likeCurrentContent"
      />
    </div>
  </article>

  <section id="comments" v-if="hasComments || allowComment" class="comments">
    <BlogSectionTitle id="comments-title">评论区</BlogSectionTitle>

    <BlogCommentForm v-if="allowComment && !replyingTo" :content-id="content?.ID || 0" :parent-id="0" @submitted="onCommentSubmitted" />

    <div class="tree-wrap">
      <BlogCommentListSkeleton v-if="showCommentPagerSkeleton || showCommentInfiniteSkeleton" :rows="3" />
      <BlogCommentTree
        v-else
        :comments="comments"
        :gravatar-mirror="gravatarMirror"
        :replying-id="replyingTo?.ID"
        :allow-reply="allowComment"
        @reply="onReply"
      >
        <template v-if="allowComment" #reply-form="{ item }">
          <BlogCommentForm
            :content-id="content?.ID || 0"
            :parent-id="item.ID"
            :reply-author="replyingTo?.author || '匿名用户'"
            @cancel-reply="clearReply"
            @submitted="payload => onCommentSubmitted(payload, item)"
          />
        </template>
      </BlogCommentTree>
      <BlogPager
        v-if="isCommentPagerMode"
        :page="currentCommentPage"
        :page-size="commentPageSize"
        :total="commentTotal"
        query-key="cpage"
        scroll-target="#comments-title"
      />
      <template v-else>
        <BlogCommentListSkeleton v-if="commentLoading" :rows="2" />
        <BlogLoadingIndicator v-if="commentLoading" />
        <div v-else-if="hasMoreComments" class="comment-more-tip">下滑加载更多评论</div>
      </template>
    </div>
  </section>
</template>

<style scoped>
.page-detail {
  padding: 20px;
}

h1 {
  font-size: var(--heading-title-size);
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
  margin-bottom: 14px;
  color: var(--text-soft);
  font-size: 13px;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.page-tools {
  margin-top: 12px;
}

.toc-box {
  margin-bottom: 14px;
}

.comments {
  margin-top: 16px;
  display: grid;
  gap: 12px;
}

.html-view :deep(img) {
  max-width: 100%;
  border-radius: 10px;
  cursor: pointer;
}

.tree-wrap {
  display: grid;
  gap: 12px;
}

.comment-more-tip {
  justify-self: center;
  font-size: 12px;
  color: var(--text-soft);
}
</style>
